import React, { useEffect, useRef, useState } from 'react';
import { ArrowRightOutlined } from '@ant-design/icons';
import { trackEvent } from '../../utils/analytics';
import { VIDEO_TILE } from './homeContent';
import './HomeVideoTile.css';

/**
 * HomeVideoTile — the hire-journey film as a click-to-play TILE directly
 * after the demo frame (round-6 ruling: the video moves below the fold; the
 * demo frame is the fold's proof now). Same design the tile always had —
 * thumbnail, copy, arrow — spanning the frame's width.
 *
 * SHARED WITH /ai-employees since Sep 9 2026 (Nitish's direction): that page
 * mounts the same tile with the product-tour film. Props:
 *   content    { title, dur, blurb, poster, src } (default homeContent.VIDEO_TILE)
 *   page       event attribution (page + compare_page); default 'home'
 *   video      the video_progress series id; default 'home-hero'. A new film on
 *              a new page gets a NEW id — a series is never reused.
 *   playEvent  the cta_click name fired on open; default 'hero_video_tile_play'
 *
 * Click opens a lightbox and plays the film WITH SOUND (a tile click is the
 * deliberate-watch action, unlike the old muted autoplay). The lightbox adds
 * body.cc-modal-open so the mobile sticky CTA slides away (one ask at a time).
 *
 * Analytics: hero_video_tile_play on open; the existing video_progress_25/50/
 * 75/100 milestones keep their names (video:'home-hero') with
 * location:'video_tile' so the funnel reads continue.
 *
 * CEL-1363 (poster swap to a real hire-film frame) rides this file: the
 * poster URL lives in homeContent.js.
 */
const MILESTONES = [25, 50, 75, 99];

const HomeVideoTile = ({
  content = VIDEO_TILE,
  page = 'home',
  video = 'home-hero',
  playEvent = 'hero_video_tile_play',
}) => {
  const [open, setOpen] = useState(false);
  const videoRef = useRef(null);
  const firedRef = useRef({});

  const openBox = () => {
    trackEvent('cta_click', { cta_name: playEvent, page, compare_page: page, location: 'video_tile' });
    setOpen(true);
  };
  const closeBox = () => setOpen(false);

  useEffect(() => {
    if (!open) return undefined;
    document.body.classList.add('cc-modal-open');
    const v = videoRef.current;
    if (v) {
      v.currentTime = 0;
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    }
    const onKey = (e) => { if (e.key === 'Escape') closeBox(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.classList.remove('cc-modal-open');
      document.removeEventListener('keydown', onKey);
      if (v) v.pause();
    };
  }, [open]);

  const onTime = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    const pct = (v.currentTime / v.duration) * 100;
    MILESTONES.forEach((m) => {
      if (pct >= m && !firedRef.current[m]) {
        firedRef.current[m] = true;
        trackEvent(`video_progress_${m === 99 ? 100 : m}`, {
          video, page, compare_page: page, location: 'video_tile',
        });
      }
    });
  };

  return (
    <>
      <div className="home-vtile-wrap">
        <button type="button" className="home-vtile" onClick={openBox}>
          <span className="home-vtile__thumb">
            <img src={content.poster} alt="" loading="lazy" />
            <span className="home-vtile__play" aria-hidden="true">&#9654;</span>
          </span>
          <span className="home-vtile__copy">
            <b>{content.title} <span className="home-vtile__dur">{content.dur}</span></b>
            <span>{content.blurb}</span>
          </span>
          <span className="home-vtile__arrow" aria-hidden="true"><ArrowRightOutlined /></span>
        </button>
      </div>

      {/* the lightbox — [hidden] paired with display:none (an unpaired display
          rule is how a hidden overlay ends up eating clicks) */}
      <div className="home-vlight" hidden={!open}>
        <div className="home-vlight__scrim" onClick={closeBox} role="presentation" />
        <div className="home-vlight__card" role="dialog" aria-modal="true" aria-label={content.title}>
          <button type="button" className="home-vlight__x" onClick={closeBox} aria-label="Close">&#10005;</button>
          {/* video mounts only while open, so the closed page never holds an
              armed <video> (the prerender-safety doctrine) */}
          {open && (
            <video
              ref={videoRef}
              controls
              playsInline
              preload="auto"
              poster={content.poster}
              src={content.src}
              onTimeUpdate={onTime}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default HomeVideoTile;
